import { Box, Button, Typography } from "@mui/material";
import React from "react";
import ImageUploading, { ImageType } from "react-images-uploading";
import useResponsive from "../hooks/useResponsive";
import imageCompression from "browser-image-compression";
import { ImageListType } from "react-images-uploading";
import { getBase64Dimensions, getImageDimensions, resizeFile } from "../utils/image-size";

// Define a type for our processed image that includes all the properties we need
interface ProcessedImage extends ImageType {
  data_url: string;
  file: File;
}


// Helper function to convert base64 to File object
const base64ToFile = async (base64String: string, originalFileName: string): Promise<File> => {
  const response = await fetch(base64String);
  const blob = await response.blob();
  return new File([blob], originalFileName, { type: 'image/jpeg' });
};

export function ImageUpload({
  multiple,
  styles,
  onSelect,
  showText,
}: {
  multiple: boolean;
  styles: any;
  showText: boolean;
  onSelect: (image: ProcessedImage[]) => void;
}) {
  const [images, setImages] = React.useState<ProcessedImage[]>([]);
  const maxNumber = 69;
  const isMedium = useResponsive("down", "md", "xl");
  const [debug, setDebug] = React.useState<{
    original?: { width: number; height: number };
    resized?: { width: number; height: number };
  }>({});

  const onChange = async (
    imageList: ImageListType,
    addUpdateIndex: number[] | undefined
  ) => {
    try {
      const processedImages = await Promise.all(
        imageList.map(async (image) => {
          if (!image.file) {
            console.warn('Image file is missing');
            return null;
          }

          try {
              // Get original dimensions
              const originalDimensions = await getImageDimensions(image.file);
  
              // First resize the image
              const resizedBase64 = await resizeFile(image.file);
              
              // Get dimensions after resize
              const resizedDimensions = await getBase64Dimensions(resizedBase64);
  
              // Update debug state
              setDebug({
                original: originalDimensions,
                resized: resizedDimensions
              });
  
            // First resize the image
            
            
            // Convert resized base64 to File
            const response = await fetch(resizedBase64);
            const blob = await response.blob();
            const resizedFile = new File([blob], image.file.name, { type: 'image/jpeg' });

            // Compress the resized file
            const compressedFile = await imageCompression(resizedFile, {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            });

            // Get final compressed version as base64
            const compressedBase64 = await imageCompression.getDataUrlFromFile(compressedFile);

            // Return only what handle_images needs
            return {
              ...image,
              data_url: compressedBase64, // This is the final, optimized version
              file: compressedFile        // This is the final, optimized file
            };
          } catch (processingError) {
            console.error("Image processing error:", processingError);
            return null;
          }
        })
      );
      
      // Filter out any failed processing attempts
      const validProcessedImages = processedImages.filter((img): img is ProcessedImage => img !== null);
      
      setImages(validProcessedImages);
      onSelect(validProcessedImages); // This will work directly with handle_images
    } catch (error) {
      console.error("Overall image processing error:", error);
    }
  };
  return (
    <ImageUploading
      value={images}
      multiple={multiple}
      onChange={onChange}
      maxNumber={maxNumber}
      dataURLKey="data_url"
    >
      {({
        imageList,
        onImageUpload,
        onImageRemoveAll,
        onImageUpdate,
        onImageRemove,
        isDragging,
        dragProps,
      }) => (
        <div
          className="upload__image-wrapper"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {showText && (
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
              Valid voter's card
            </Typography>
          )}
          <Button
            style={isDragging ? { color: "red" } : undefined}
            onClick={onImageUpload}
            sx={{
              height: 200,
              width: isMedium ? "94%" : 400,
              backgroundColor: "#E2F1EB",
              border: `1px dotted #459173`,
              display: "block",
              ...styles,
            }}
            {...dragProps}
          >
            <img
              src="/assets/icons/cloud-upload.svg"
              width={48}
              height={48}
              alt="i"
            />
            <Typography color="black" sx={{ fontWeight: 600 }}>
              Drag & drop files or
              <span style={{ color: "#459173", textDecoration: "underline" }}>
                {" "}
                Browse
              </span>
            </Typography>
            <Typography
              sx={{
                color: "#676767",
                fontWeight: 400,
                fontSize: 12,
              }}
            >
              Supported formats: JPEG, PNG & PDF
            </Typography>
          </Button>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {images.map((image, index) => (
              <div key={index} className="image-item">
                <img src={image["data_url"]} alt="" width="100" />
                <div className="image-item__btn-wrapper">
                  <Button onClick={() => onImageUpdate(index)}>Update</Button>
                  <Button onClick={() => onImageRemove(index)}>Remove</Button>
                </div>
              </div>
            ))}
          </Box>
        </div>
      )}
    </ImageUploading>
  );
}